import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@components/Box';
import {
  IconTextPairing,
  StateIconTextContainer,
  StyledTextPairing,
  StyledWrapper,
  LogosWrapper,
  LogosAnimation,
  LogoBox,
  StyledLogo,
  StyledLogoWrapper,
} from './RegistrySection.styles';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { CloudImage } from '@components/CloudImage';
import { Flex } from '@components/Flex';
import { SpacingStack } from '@components/SpacingStack';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useIsMounted } from '@hooks/useIsMounted';
import { gsap, Linear } from 'gsap';
import ScrollMagic from 'scrollmagic';
import ReactHtmlParser from 'react-html-parser';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { useTranslation } from 'react-i18next';

const smallTitleProps = {
  titleVariant: 'featureHed17',
  bodyVariant: 'featureCopy17',
  titleColor: 'neutra6',
  alignment: 'center',
} as const;

export const RegistrySection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const tagsRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const [isVisible, setIsVisible] = useState(false);

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let heroTimeline: ReturnType<typeof gsap.timeline>;

    if (scrollController && isMounted) {
      const initTimeline = () => {
        if (scrollController && tagsRef.current) {
          heroTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
          heroTimeline.fromTo(
            tagsRef.current,
            {
              transform: 'translateX(30vh)',
            },
            {
              transform: 'translateX(-130vh)',
            },
            0
          );
        }
      };
      const initScene = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: '#logos-wrapper',
          triggerHook: 'onEnter',
          duration: '200%',
        })
          .addTo(scrollController)
          // workaround from here https://github.com/janpaepke/ScrollMagic/issues/918 until plugin for gsap 3 released https://github.com/janpaepke/ScrollMagic/pull/920 or we update it locally
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            heroTimeline?.progress(event.progress);
          });
      };
      initTimeline();
      initScene();
    }
    const clearScene = () => {
      if (heroTimeline) {
        heroTimeline.progress(0);
        heroTimeline.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return () => {
      clearScene();
    };
  }, [scrollController, isMounted]);

  const { t } = useTranslation('babyRegistry');
  const universalRegistry = t('universalRegistry');

  return (
    <Box as="section" id="BabyRegistrySection" backgroundColor="yellow1" paddingY={[4, 6]} ref={sectionRef}>
      <LazyLoadWrapper beforeLoad={() => setIsVisible(true)}>
        <div />
      </LazyLoadWrapper>
      <StyledWrapper
        flexDirection="column"
        justifyContent="center"
        bgTopLeft={isVisible ? cloudAssetUrl(universalRegistry.bgTopLeft, { optimization: 'default' }) : ''}
        bgBottomRight={isVisible ? cloudAssetUrl(universalRegistry.bgBottomRight, { optimization: 'default' }) : ''}
        paddingY={[4, 10]}
      >
        <StyledTextPairing
          eyebrow={universalRegistry.eyebrow}
          eyebrowVariant="sectionEyeBrow20"
          eyebrowColor="neutra4"
          titleVariant="hed56"
          titleColor="neutra6"
          titleTagName="h3"
          title={ReactHtmlParser(universalRegistry.title)}
          body={universalRegistry.body}
          bodyVariant="dek20"
          bodyColor="neutra5"
          alignment="center"
        />
        <Flex marginTop={[6, 8]} justifyContent={'center'}>
          <StateIconTextContainer>
            <CloudImage src="baby-registry/universal-reg/icon-gifts.svg" height={32} optimization="default" />
            <IconTextPairing title={universalRegistry.registryTypeGifts} {...smallTitleProps} />
          </StateIconTextContainer>
          <StateIconTextContainer>
            <CloudImage src="baby-registry/universal-reg/icon-funds.svg" height={32} optimization="default" />
            <IconTextPairing title={universalRegistry.registryTypeCash} {...smallTitleProps} />
          </StateIconTextContainer>
          <StateIconTextContainer>
            <CloudImage src="baby-registry/universal-reg/icon-babymoon.svg" height={32} optimization="default" />
            <IconTextPairing title={universalRegistry.registryTypeBabymoon} {...smallTitleProps} />
          </StateIconTextContainer>
        </Flex>
      </StyledWrapper>
      <Flex marginTop={[-2]} backgroundColor="yellow1">
        <LogosWrapper id="logos-wrapper" ref={tagsRef}>
          <LogosAnimation alignItems="center">
            <SpacingStack>
              <StyledLogoWrapper alignItems="center">
                {[...universalRegistry.logos, ...universalRegistry.logos].map((item, index) => (
                  <LogoBox key={index} marginX={item.width === 50 ? 0 : [6, 10, 10, 10, 12]}>
                    <StyledLogo alt={item.alt} src={item.src} width={item.width} optimization="default" />
                  </LogoBox>
                ))}
              </StyledLogoWrapper>
            </SpacingStack>
          </LogosAnimation>
        </LogosWrapper>
      </Flex>
    </Box>
  );
};
